import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';

const DARK_RS_COLORS = {
  offWhite: '#e9ede6',
  offBlack: '#2a2e28',
  jet: '#363b33',
  jetLight: '#566151',
  gridLine: 'rgba(243,244,246,0.15)',
  green: '#80ed40',
  darkGreen: '#5a9f1f',
  red: '#ef4444',
} as const;

const LIGHT_RS_COLORS = {
  offWhite: '#1e2019',
  offBlack: '#f0f2ee',
  jet: '#e0e3de',
  jetLight: '#c0c8be',
  gridLine: 'rgba(0,0,0,0.10)',
  green: '#3a8c1a',
  darkGreen: '#2d5c0a',
  red: '#dc2626',
} as const;

export type RsChartColors = {
  offWhite: string;
  offBlack: string;
  jet: string;
  jetLight: string;
  gridLine: string;
  green: string;
  red: string;
};

interface ThemeContextValue {
  isDark: boolean;
  toggle: () => void;
  rsColors: RsChartColors;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDark: true,
  toggle: () => {},
  rsColors: DARK_RS_COLORS,
});

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  defaultDark?: boolean;
}> = ({ children, defaultDark = true }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme-preference');
    if (saved === 'light') return false;
    if (saved === 'dark') return true;
    return defaultDark;
  });

  useEffect(() => {
    localStorage.setItem('theme-preference', isDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDark ? '' : 'light');
  }, [isDark]);

  const toggle = useCallback(() => setIsDark(prev => !prev), []);

  const rsColors = useMemo(
    () => (isDark ? DARK_RS_COLORS : LIGHT_RS_COLORS),
    [isDark],
  );

  return (
    <ThemeContext.Provider value={{ isDark, toggle, rsColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
